import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, LoaderCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errors';
import { ApiError } from '@/api/client';
import { Button } from './Button';

export function Spinner({ className, label }: { className?: string; label?: string }) {
  const { t } = useTranslation();
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-ink-3', className)}>
      <LoaderCircle className="size-4 animate-spin" aria-hidden />
      <span className={label ? 'text-sm' : 'sr-only'}>{label ?? t('common.loading')}</span>
    </span>
  );
}

/** Skeleton rows that keep layout stable while loading. */
export function LoadingBlock({ rows = 4, className }: { rows?: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t('common.loading')} className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-[var(--radius-ctl)] bg-surface-3" style={{ opacity: 1 - i * 0.15 }} />
      ))}
    </div>
  );
}

export function PageLoading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner />
    </div>
  );
}

export function EmptyState({
  icon, title, children, action, className, compact,
}: { icon?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode; className?: string; compact?: boolean }) {
  return (
    <div
      className={cn(
        'flex flex-col items-start gap-3 rounded-[var(--radius-panel)] border border-dashed border-line-strong bg-surface/60',
        compact ? 'p-4' : 'p-6 sm:p-8',
        className,
      )}
    >
      {icon && <div className="text-ink-3 [&>svg]:size-6">{icon}</div>}
      <div className="max-w-prose">
        <h3 className="text-base font-semibold text-ink">{title}</h3>
        {children && <div className="mt-1 text-sm text-ink-2">{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const { t } = useTranslation();
  const notFound = error instanceof ApiError && (error.status === 404 || error.status === 403);
  return (
    <div role="alert" className={cn('flex flex-col items-start gap-3 rounded-[var(--radius-panel)] border border-suspicious/40 bg-suspicious-soft p-5', className)}>
      <div className="flex items-start gap-2 text-suspicious">
        <CircleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
        <div>
          <p className="font-semibold">{notFound ? t('errors.notFoundTitle') : t('errors.loadFailed')}</p>
          <p className="mt-0.5 text-sm text-ink-2">{errorMessage(error)}</p>
        </div>
      </div>
      {onRetry && !notFound && (
        <Button size="sm" variant="secondary" onClick={onRetry} icon={<RefreshCw className="size-3.5" />}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

/** Renders loading / error / content for a react-query result. */
export function QueryState<T>({
  query, children, loading,
}: {
  query: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  children: (data: T) => ReactNode;
  loading?: ReactNode;
}) {
  if (query.isPending) return <>{loading ?? <LoadingBlock />}</>;
  if (query.isError || query.data === undefined) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <>{children(query.data)}</>;
}
